import {BehaviorSubject, Observable, of, shareReplay, skipWhile, Subject} from "rxjs";
import Pagination from "../model/pagination";
import {environment} from "../environments/environment";
import QueryEncoder from "./queryEncoder";
import {HttpClient} from "@angular/common/http";
import {catchError, switchMap} from "rxjs/operators";
import Crypto from "../helper/crypto";
import {AppCache, cache} from "../cache";

export default abstract class GenericCrudService<T> {
    protected cache: AppCache;

    protected showShareReplays: {
        [x: string]: Observable<T>
    } = {};
    public constructor(protected http: HttpClient) {
        this.cache = cache;
    }
    public list(query?: string, page?: number, limit?: number, sort?: string, direction?: string, additionalInfo?: any, noCache?: boolean): Observable<Pagination<T>> {
        query = query || '';
        page = page || 1;
        limit = limit || 15;
        sort = sort || '';
        noCache = noCache || false;

        let sorting = {};
        sorting[sort] = direction;

        let q = {query, page, limit, sorting};

        if(additionalInfo) {
            Object.assign(q, additionalInfo);
        }

        const key = 'cache-' + Crypto.SHA1(this.getApiName() + '?' + JSON.stringify(q));
        const subject = new BehaviorSubject<Pagination<T>>(null);

        if(page == 1 && !noCache) {
            this.cache.getCacheItem(key).then(async cacheResult => {
                if(cacheResult?.result) {
                    let result = JSON.parse(cacheResult.result);
                    for(let i = 0; i < result.results.length; i++) {
                        let key = 'cache-' + Crypto.SHA1(this.getApiName() + '-' + (result.results[i] as any).id);
                        let existing = await this.cache.getCacheItem(key);
                        if(existing) {
                            result.results[i] = JSON.parse(existing.result);
                        }
                    }
                    result['cachedVersion'] = true;
                    if(subject.getValue() == null) {
                        subject.next(result);
                    }
                }
            });
        }

        this.http.get<Pagination<T>>(environment.apiUrl + '/' + this.getApiName() + '?' + QueryEncoder.encodeURI(q)).subscribe(result => {
            subject.next(result);
            try {
                this.cache.removeCacheItem(key).then(_ => {
                    this.cache.insertCacheItem({
                        id: key,
                        result: JSON.stringify(result)
                    });
                });
            } catch(e) {

            }

        });

        return subject.asObservable().pipe(skipWhile(e => {
            return e == null
        }));
    }

    public show(id: any, useCache?: boolean): Observable<T> {
        const key = 'cache-' + Crypto.SHA1(this.getApiName() + '-' + id);
        const subject = new BehaviorSubject<T>(null);

        if(useCache) {
            this.cache.getCacheItem(key).then(result => {
                if(result) {
                    subject.next(JSON.parse(result.result));
                }
            });
        }

        let shareReplayKey = this.getApiName() + '-' + id;
        if(shareReplayKey in this.showShareReplays && useCache) {
            this.showShareReplays[shareReplayKey].subscribe(result => {
                subject.next(result);
                this.cache.insertCacheItem({
                    id: key,
                    result: JSON.stringify(result)
                });
            });
        } else {
            this.showShareReplays[shareReplayKey] = this.http.get<T>(environment.apiUrl + '/' + this.getApiName() + '/' + id)
                .pipe(shareReplay());
        }

        this.showShareReplays[shareReplayKey].subscribe(result => {
            subject.next(result);
            this.cache.insertCacheItem({
                id: key,
                result: JSON.stringify(result)
            });
        });



        return subject.asObservable().pipe(skipWhile(e => {
            return e == null
        }));
    }

    public create(item: T): Observable<T> {
        return this.http.post<T>(environment.apiUrl + '/' + this.getApiName(), item).pipe(switchMap(result => {
            const key = 'cache-' + Crypto.SHA1(this.getApiName() + '-' + (result as any).id);
            this.cache.insertCacheItem({
                id: key,
                result: JSON.stringify(result)
            });
            return of(result);
        }));
    }

    public delete(item: T): Observable<boolean> {
        return this.http.delete(environment.apiUrl + '/' + this.getApiName() + '/' + (item as any).id)
            .pipe(switchMap(_ => {
                const key = 'cache-' + Crypto.SHA1(this.getApiName() + '-' + (item as any).id);
                this.cache.removeCacheItem(key);
                return of(true)
            }), catchError(_ => of(false)));
    }

    public update(item: T): Observable<T> {
        return this.http.put<T>(environment.apiUrl + '/' + this.getApiName() + '/' + (item as any).id, item).pipe(switchMap(result => {
            const key = 'cache-' + Crypto.SHA1(this.getApiName() + '-' + (result as any).id);
            this.cache.insertCacheItem({
                id: key,
                result: JSON.stringify(result)
            });
            return of(result);
        }));
    }

    public abstract getApiName();
}
